import React, { useState, useEffect } from "react";
import { getToken } from "../../utils/tokenUtils";
import {
  Calendar,
  Users,
  Play,
  CheckCircle,
  AlertCircle,
  Clock,
  DollarSign,
  TrendingUp,
  FileText,
} from "lucide-react";
import { useEmploymentDetails } from "../../contexts/EmploymentManagementContext";
import Notification from "../../components/UI/Notification";

const PayrollGeneration = () => {
  const { employmentDetails, pagination, loading, loadMoreEmploymentDetails } =
    useEmploymentDetails();
  const [notification, setNotification] = useState(null);
  const [generationData, setGenerationData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    employee_ids: [],
  });
  const [summary, setSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  // const [employees, setEmployees] = useState(employmentDetails);
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const token = getToken();

  useEffect(() => {
    fetchSummary();
    // fetchEmployees();
  }, [generationData.year, generationData.month]);

  const fetchSummary = async () => {
    try {
      const response = await fetch(
        "https://hrmsnew.prepstripe.com/api/payroll/reports/summary",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            year: generationData.year,
            month: generationData.month,
          }),
        },
      );
      const data = await response.json();
      setSummary(data.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const handleGeneratePayroll = async () => {
    if (!selectAll && selectedEmployees.size === 0) {
      setNotification({
        message: "Please select employee(s) to generate payroll",
        type: "error",
      });
      return;
    }

    try {
      setGenerating(true);
      setGenerationResult(null);

      const payload = {
        year: generationData.year,
        month: generationData.month,
        ...(selectedEmployees.size > 0 && {
          employee_ids: Array.from(selectedEmployees),
        }),
      };

      const token = getToken();
      const response = await fetch(
        "https://hrmsnew.prepstripe.com/api/payroll/bulk-generate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();

      setNotification({
        message: result.message,
        type: "success",
      });

      setGenerationResult(result);

      // Refresh summary after generation
      await fetchSummary();
    } catch (error) {
      console.error("Error generating payroll:", error);
      setGenerationResult({
        success: false,
        message: "Failed to generate payroll",
        errors: [error.message],
      });
      setNotification({
        message: error.message,
        type: "error",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleEmployeeSelection = (employeeId) => {
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);
    setSelectAll(newSelected.size === employmentDetails.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees(new Set());
      setSelectAll(false);
    } else {
      setSelectedEmployees(
        new Set(employmentDetails.map((emp) => emp.employee_id)),
      );
      setSelectAll(true);
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "warning":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <Clock className="h-5 w-5 text-blue-500" />;
    }
  };

  return (
    <div className="p-3">
      {/* notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Generate Payroll</h1>
          <p className="text-gray-600 mt-2">
            Process monthly payroll for selected period and employees
          </p>
        </div>

        {/* Quick Stats Cards */}
        {summary && (
          // <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-6">
          //   <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg shadow-sm border border-gray-200 p-4">
          //     <div className="flex items-center">
          //       <div className="p-2 bg-green-500 rounded-lg">
          //         <DollarSign className="h-6 w-6 text-white" />
          //       </div>
          //       <div className="ml-4">
          //         <p className="text-sm font-medium text-gray-700">
          //           Total Net Salary
          //         </p>
          //         <p className="text-xl font-bold text-gray-900">
          //           ₹{summary.total_net_salary?.toLocaleString() || "0"}
          //         </p>
          //       </div>
          //     </div>
          //   </div>

          //   <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg shadow-sm border border-gray-200 p-4">
          //     <div className="flex items-center">
          //       <div className="p-2 bg-blue-500 rounded-lg">
          //         <Users className="h-6 w-6 text-white" />
          //       </div>
          //       <div className="ml-4">
          //         <p className="text-sm font-medium text-gray-700">
          //           Total Employees
          //         </p>
          //         <p className="text-xl font-bold text-gray-900">
          //           {summary.total_employees}
          //         </p>
          //       </div>
          //     </div>
          //   </div>

          //   <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg shadow-sm border border-gray-200 p-4">
          //     <div className="flex items-center">
          //       <div className="p-2 bg-purple-500 rounded-lg">
          //         <TrendingUp className="h-6 w-6 text-white" />
          //       </div>
          //       <div className="ml-4">
          //         <p className="text-sm font-medium text-gray-700">
          //           Total Gross Salary
          //         </p>
          //         <p className="text-xl font-bold text-gray-900">
          //           ₹{summary.total_gross_salary?.toLocaleString() || "0"}
          //         </p>
          //       </div>
          //     </div>
          //   </div>

          //   <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg shadow-sm border border-gray-200 p-4">
          //     <div className="flex items-center">
          //       <div className="p-2 bg-orange-500 rounded-lg">
          //         <AlertCircle className="h-6 w-6 text-white" />
          //       </div>
          //       <div className="ml-4">
          //         <p className="text-sm font-medium text-gray-700">
          //           Total Deductions
          //         </p>
          //         <p className="text-xl font-bold text-gray-900">
          //           ₹{summary.total_deductions?.toLocaleString() || "0"}
          //         </p>
          //       </div>
          //     </div>
          //   </div>
          // </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 mb-2">
            <div className="bg-white rounded-md shadow-md p-2">
              <div className="flex items-center">
                <div className="p-1.5 bg-green-500 rounded-lg">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    Total Net Salary
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    ₹{summary.total_net_salary?.toLocaleString() || "0"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-md shadow-md p-2">
              <div className="flex items-center">
                <div className="p-1.5 bg-blue-500 rounded-lg">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    Total Employees
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {summary.total_employees}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-md shadow-md p-2">
              <div className="flex items-center">
                <div className="p-1.5 bg-purple-500 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    Total Gross Salary
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    ₹{summary.total_gross_salary?.toLocaleString() || "0"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-md shadow-md p-2">
              <div className="flex items-center">
                <div className="p-1.5 bg-orange-500 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    Total Deductions
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    ₹{summary.total_deductions?.toLocaleString() || "0"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          {/* Left Panel - Configuration */}
          {/* <div className="bg-red-400"> */}
          {/* Period Selection */}
          <div className="flex gap-x-3 w-full">
            <div className="bg-white w-3/5 rounded-md  shadow-md p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Payroll Period
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={generationData.year}
                    onChange={(e) =>
                      setGenerationData((prev) => ({
                        ...prev,
                        year: parseInt(e.target.value),
                      }))
                    }
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Month
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={generationData.month}
                    onChange={(e) =>
                      setGenerationData((prev) => ({
                        ...prev,
                        month: parseInt(e.target.value),
                      }))
                    }
                  >
                    {months.map((month, index) => (
                      <option key={index} value={index + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="bg-white w-2/5 rounded-md shadow-md px-4 py-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Period Summary
              </h3>

              <div className="text-center py-4 border border-gray-200 rounded-lg mb-4">
                <h4 className="text-xl font-bold text-gray-900">
                  {summary?.period ||
                    `${months[generationData.month - 1]} ${
                      generationData.year
                    }`}
                </h4>
                <p className="text-sm text-gray-600">Selected Period</p>
              </div>

              {summary && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">
                      Total Employees
                    </span>
                    <span className="font-semibold text-gray-900">
                      {summary.total_employees || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">
                      Total Basic Salary
                    </span>
                    <span className="font-semibold text-gray-900">
                      ₹{summary.total_basic_salary?.toLocaleString() || "0"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">
                      Total Gross Salary
                    </span>
                    <span className="font-semibold text-gray-900">
                      ₹{summary.total_gross_salary?.toLocaleString() || "0"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">
                      Total Earnings
                    </span>
                    <span className="font-semibold text-green-600">
                      ₹{summary.total_earnings?.toLocaleString() || "0"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">
                      Total Deductions
                    </span>
                    <span className="font-semibold text-red-600">
                      ₹{summary.total_deductions?.toLocaleString() || "0"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">
                      Total Tax Deducted
                    </span>
                    <span className="font-semibold text-orange-600">
                      ₹{summary.total_tax_deducted?.toLocaleString() || "0"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-blue-50 px-3 rounded-md">
                    <span className="text-sm font-medium text-blue-800">
                      Total Net Salary
                    </span>
                    <span className="font-bold text-blue-900">
                      ₹{summary.total_net_salary?.toLocaleString() || "0"}
                    </span>
                  </div>

                  {/* Status Breakdown */}
                  {summary.status_breakdown && (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Status Breakdown:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(summary.status_breakdown).map(
                          ([status, count]) => (
                            <span
                              key={status}
                              className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 capitalize"
                            >
                              {status}: {count}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedEmployees.size > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      Selected Employees
                    </span>
                    <span className="font-semibold text-blue-600">
                      {selectedEmployees.size}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Employee Selection */}
          <div className="bg-white rounded-md shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Employee Selection
            </h3>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Select All Employees ({employmentDetails.length})
                </span>
              </label>
            </div>

            {/* employee data table */}
            <div
              className="max-h-64 overflow-y-auto border border-gray-200 rounded-md"
              onScroll={(e) => {
                const el = e.currentTarget;
                const nearBottom =
                  el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
                if (nearBottom) {
                  loadMoreEmploymentDetails();
                }
              }}
            >
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Select
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      EMP ID
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Department
                    </th>
                    {/* <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Basic Salary</th> */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employmentDetails.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={selectedEmployees.has(employee.employee_id)}
                          onChange={() =>
                            handleEmployeeSelection(employee.employee_id)
                          }
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div>
                          <div className="text-sm text-gray-500">
                            {employee.employee_id}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {employee.department}
                      </td>
                      {/* <td className="px-4 py-2 text-sm text-gray-900">₹{employee.basic_salary?.toLocaleString()}</td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && (
                <div className="py-2 text-center text-sm text-gray-500">
                  Loading…
                </div>
              )}
              {pagination &&
                pagination.currentPage >= pagination.totalPages &&
                !loading && (
                  <div className="py-2 text-center text-xs text-gray-400">
                    No more records
                  </div>
                )}
            </div>

            <div className="mt-4 text-sm text-gray-600">
              {selectedEmployees.size === 0
                ? "All employees will be processed"
                : `${selectedEmployees.size} employee(s) selected`}
            </div>
          </div>

          {/* Generation Actions */}
          <div className="w-1/2 mx-auto bg-white rounded-md shadow-md p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              Actions
            </h3>

            <div className="space-y-3">
              <button
                onClick={handleGeneratePayroll}
                disabled={generating}
                className={`w-10/12 mx-auto flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  generating
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg"
                } text-white`}
              >
                {generating ? (
                  <>
                    <Clock className="h-5 w-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Generate Payroll
                  </>
                )}
              </button>

              <div className="text-center text-sm text-gray-500">
                <p>This will process payroll for:</p>
                <p className="font-medium">
                  {selectedEmployees.size > 0
                    ? `${selectedEmployees.size} selected employee(s)`
                    : "All employees"}
                </p>
              </div>
            </div>
          </div>

          {/* Generation Progress */}
          {generating && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 animate-spin" />
                Processing Payroll...
              </h3>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full animate-pulse"
                  style={{ width: "70%" }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Please wait while we process the payroll...
              </p>
            </div>
          )}
          {/* </div> */}

          {/* Right Panel - Summary & Actions */}
          <div className="space-y-6">
            {/* Period Summary */}

            {/* Quick Stats Cards */}
            {/* {summary && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-green-700">Total Net Salary</p>
                      <p className="text-xl font-bold text-green-900">₹{summary.total_net_salary?.toLocaleString() || '0'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-blue-700">Total Employees</p>
                      <p className="text-xl font-bold text-blue-900">{summary.total_employees}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg shadow-sm border border-purple-200 p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-500 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-purple-700">Total Gross Salary</p>
                      <p className="text-xl font-bold text-purple-900">₹{summary.total_gross_salary?.toLocaleString() || '0'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg shadow-sm border border-orange-200 p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-orange-500 rounded-lg">
                      <AlertCircle className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-orange-700">Total Deductions</p>
                      <p className="text-xl font-bold text-orange-900">₹{summary.total_deductions?.toLocaleString() || '0'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )} */}

            {/* Pre-generation Checklist */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pre-generation Checklist</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Salary structures are up to date</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Attendance data is available</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Payroll components are configured</span>
                </div>
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-500 mr-3 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Review any pending changes</span>
                </div>
              </div>
            </div> */}

            {/* Recent Generation History */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Generations</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-900">December 2024</p>
                    <p className="text-xs text-gray-500">Generated on 31/12/2024</p>
                  </div>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Completed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-900">November 2024</p>
                    <p className="text-xs text-gray-500">Generated on 30/11/2024</p>
                  </div>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Completed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">October 2024</p>
                    <p className="text-xs text-gray-500">Generated on 31/10/2024</p>
                  </div>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Completed
                  </span>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Generation Tips */}
        {/* <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Generation Tips
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <p className="font-medium mb-2">Before Generation:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Ensure all salary structures are finalized</li>
                <li>Verify attendance data is complete</li>
                <li>Check for any pending salary revisions</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-2">After Generation:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Review generated payroll records</li>
                <li>Approve payroll before processing payments</li>
                <li>Generate and distribute payslips</li>
              </ul>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default PayrollGeneration;
